import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link, useNavigate } from 'react-router';
import { CellValueChangedEvent } from 'ag-grid-community';
import DialogUsers from '@/components/DialogUsers/DialogUsers';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import InputUser from '@/components/UI/InputUser/InputUser';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import ExternalLinkFormatter from '@/components/UI/TableListEditable/ExternalLinkFormatter';
import { ALIGN, CELL_EDITOR_TYPES, CELL_RENDERER_TYPES } from '@/components/UI/TableListEditable/TableListEditableConstants';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { ASC_ORDER, ERROR_TYPES_ADD_STANDIN, PROD_ALICE, TEAMS_PERSON, TEST_ALICE, errorRoleToFormatedRole, errorTypes, getAbsoluteRoute, isNotPRO } from '@/helpers/constants';
import { backFormatRoleToCleanFormat, formatSummaryUser } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAppLanguage from '@/hooks/useAppLanguage';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import { setPlantsForm } from '@/redux/Forms/forms.action';
import { getPlantsForm } from '@/redux/Forms/forms.reducer';
import titleService from '@/services/titleService';
import usersServices from '@/services/usersServices';
import { ColumnConfig, UserSearch } from '@/types';
import useQuery from '@/utils/hooks';
import styles from './StandIndsAdmin.module.scss';

const COLUMNS_NAMES = {
  MB_INSIDE_CODE: 'mbInsideCode',
  USER_INFO: 'userInfo',
  DEPARTMENT: 'department',
  PLANT: 'plant',
  ROLES: 'roles',
  SYSTEM_NOTIFICATIONS: 'systemNotifications',
  EMAIL_NOTIFICATIONS: 'emailNotifications',
};

const NormalA = ({ children, ...props }) => <a className={styles.NormalA} {...props}>{children}</a>;
const isArrayAndHasElements = (array) => Array.isArray(array) && array.length > 0;

const StandIndsAdmin = () => {
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const userSelectedURL = useQuery().get('userSelected');
  const navigate = useNavigate();
  const { addToast } = useToastContext();
  const { setLoading: setGlobalLoading } = useGlobalSpinnerContext();
  const [loading, setLoading] = useState(false);
  const [loadingStandIns, setLoadingStandIns] = useState(false);
  const [isNewStandInOpen, setNewStandInOpen] = useState(false);
  const [userSelected, setUserSelected] = useState<UserSearch | null>(null);
  const [standInsUserSelected, setStandInsUserSelected] = useState<any[]>([]);
  const [selection, setSelection] = useState<string[]>([]);
  const fetchUsersFilterTable = useApiRequest(usersServices.getUsersFilterTable);
  const linkToAlice = isNotPRO ? TEST_ALICE : PROD_ALICE;
  const fetchStandins = useApiRequest(usersServices.getStandIns);

  const columnsStandins = useMemo<ColumnConfig[]>(() => [
    {
      name: COLUMNS_NAMES.MB_INSIDE_CODE,
      title: t('administration.standins.linkToMBSite'),
      cellRenderer: ({ value }: { value: string }) => <ExternalLinkFormatter value={value} urlFormatter={(value) => `${TEAMS_PERSON}${value}`} className={styles.NormalA} />,
    },
    { name: COLUMNS_NAMES.USER_INFO, title: t('standin.userInfo'), wrapText: true },
    { name: COLUMNS_NAMES.DEPARTMENT, title: t('administration.standins.userDepartment'), wrapText: true },
    { name: COLUMNS_NAMES.PLANT, title: t('plant'), wrapText: true },
    { name: COLUMNS_NAMES.ROLES, title: t('administration.standins.assignedRoles'), wrapText: true },
    {
      name: COLUMNS_NAMES.SYSTEM_NOTIFICATIONS,
      title: t('standin.systemNotifications'),
      cellRenderer: CELL_RENDERER_TYPES.CHECKBOX,
      cellEditor: CELL_EDITOR_TYPES.CHECKBOX,
      align: ALIGN.CENTER,
      editable: true,
    },
    {
      name: COLUMNS_NAMES.EMAIL_NOTIFICATIONS,
      title: t('standin.emailNotifications'),
      cellRenderer: CELL_RENDERER_TYPES.CHECKBOX,
      cellEditor: CELL_EDITOR_TYPES.CHECKBOX,
      align: ALIGN.CENTER,
      editable: true,
    },
  ], [t]);

  const setUserSelectedURL = (uSelected: UserSearch | null) => {
    if (uSelected) {
      navigate(`${getAbsoluteRoute('/administration/stand-ins')}?userSelected=${uSelected.userCode}`);
    } else {
      navigate(getAbsoluteRoute('/administration/stand-ins'), { replace: true });
    }
  };

  const getStandInsForUser = (useCode: string) => {
    setLoadingStandIns(true);
    fetchStandins(useCode).then((res) => {
      const standInListt = res.standInListSingleResponseByUserId?.map((standIn) => ({
        ...standIn,
        mbInsideCode: standIn.standInId,
        roles: standIn.roles.map(backFormatRoleToCleanFormat).join(', '),
      }));
      setStandInsUserSelected(standInListt);
    })
      .catch((error) => {
        if (error.name !== errorTypes.ABORT_ERROR) addToast(error.message, 'ERROR');
      }).finally(() => setLoadingStandIns(false));
  };

  const addNewStandIn = (newStandIn: UserSearch) => {
    const data = {
      userInfo: formatSummaryUser(newStandIn),
      systemNotifications: false,
      emailNotifications: false,
      organisation: newStandIn.organisation,
      standInId: newStandIn?.userCode,
      adminArea: true,
    };

    if (!standInsUserSelected.find((element) => element.standInId === data.standInId)) { // if the user isnot already selected
      setLoading(true);
      usersServices.saveStandIns(userSelected?.userCode, data)
        .then(() => getStandInsForUser(userSelected?.userCode ?? ''))
        .catch((error) => {
          if (error?.code in ERROR_TYPES_ADD_STANDIN) {
            addToast(t(`standin.${error.code}`, { role: errorRoleToFormatedRole?.[error?.message] ?? '' }), 'ERROR');
          } else {
            addToast(error.message, 'ERROR');
          }
        })
        .finally(() => {
          setLoading(false);
          setSelection([]);
        });
    }
  };

  const handleEditRow = (event: CellValueChangedEvent) => {
    const { data: rowData, colDef: { field }, newValue } = event;

    if (rowData && field) {
      setLoadingStandIns(true);
      const rowEdited = {
        ...rowData,
        [field]: newValue,
      };

      usersServices.editStandIn(userSelected?.userCode, rowEdited)
        .then(() => getStandInsForUser(userSelected?.userCode ?? ''))
        .catch((error) => {
          addToast(error.message, 'ERROR');
        })
        .finally(() => setLoadingStandIns(false));
    }
  };

  const handleDeleteStandIn = (rows: string[]) => {
    setLoadingStandIns(true);
    usersServices.deleteStandIns(userSelected?.userCode, rows)
      .then(() => getStandInsForUser(userSelected?.userCode ?? ''))
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => {
        setLoadingStandIns(false);
        setSelection([]);
      });
  };

  useEffect(() => {
    if (!userSelectedURL) {
      setUserSelected(null);
      setStandInsUserSelected([]);

      return;
    }
    setGlobalLoading(true);
    fetchUsersFilterTable({ userId: userSelectedURL }, 0, 1)
      .then(async (res) => {
        let user = res?.results?.[0];

        if (user) {
          user = await usersServices.getUserSearch(userSelectedURL);
          setUserSelected(user);
          getStandInsForUser(userSelectedURL);
        } else {
          addToast(t('administration.standins.userNotFound'), 'ERROR');
          navigate(getAbsoluteRoute('/administration/stand-ins'), { replace: true });
          setUserSelected(null);
          setStandInsUserSelected([]);
        }
      })
      .catch((error) => {
        if (error?.name !== errorTypes.ABORT_ERROR) addToast(error?.message, 'ERROR');
      }).finally(() => setGlobalLoading(false));
  }, [userSelectedURL]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    titleService.updatePageTitle(t('dashboard.administration'), t('titles.stand-in'));
  }, [t, lang]);

  return (
    <>
      <AccordionPanel block title={t('administration.standins.dataFromUserSelected')}>
        <div className={styles.StandInsPage}>
          <div className={styles.ButtonsTableLeft}>
            <div className={styles.ButtonsContentTableRight}>
              <div className={styles.OneColumns}>
                <InputUser
                  id="input-user-qmb-approver"
                  label={t('administration.standins.userSelected')}
                  value={userSelected ? formatSummaryUser(userSelected) : ''}
                  code={userSelected?.userCode ?? ''}
                  clear={() => {
                    setUserSelectedURL(null);
                    setStandInsUserSelected([]);
                  }}
                  // @ts-ignore
                  updateUserSelected={(user: UserSearch) => setUserSelectedURL(user)}
                  noSaveField
                />
              </div>
            </div>
          </div>
          {userSelected && (
            <>
              <h3>{t('administration.standins.dataFromUserSelected')}</h3>
              <div className={styles.DataUserSection} style={{ display: 'flex', justifyContent: 'flex-start', gap: '20px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
                  <div className={styles.DataUserRow}>
                    <span>{t('standin.userInfo')}:</span><span>{`${userSelected.firstName}, ${userSelected.lastName}`}</span>
                  </div>
                  <div className={styles.DataUserRow}>
                    <span>{t('administration.standins.userRoles')}:</span>
                    <span>
                      {(userSelected.rolesList && isArrayAndHasElements(userSelected.rolesList))
                        ? (userSelected.rolesList as string[]).map(backFormatRoleToCleanFormat).join(', ')
                        : <span>{t('administration.standins.noRolesUser')} <NormalA href={linkToAlice}>Alice</NormalA></span>}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div className={styles.DataUserRow}><span>{t('administration.standins.userDepartment')}:</span><span>{`${userSelected.department}`}</span></div>
                  <div className={styles.DataUserRow}>
                    <span>{t('administration.standins.userStandInList')}:</span>
                    <span>
                      {(userSelected.standInList && isArrayAndHasElements(userSelected.standInList))
                        ? (
                          <>
                            {(userSelected.standInList as { userCode: string; roles: string[] }[])
                              .map(({ userCode }, i) => (
                                <span key={userCode}>
                                  <Link className={styles.NormalA} to={`${getAbsoluteRoute('/administration/stand-ins')}?userSelected=${userCode}`}>{userCode}</Link> {i < (userSelected.standInList as unknown as string[]).length - 1 ? ', ' : ''}
                                </span>
                              ))}
                          </>
                        )
                        : t('administration.standins.noStandInsUser')}
                    </span>
                  </div>
                </div>
              </div>
              <br />
              <div className={styles.ButtonsTable}>
                <h3>{t('administration.standins.dataFromStandIn')}</h3>
                <div className={styles.ButtonsContentTableRight}>
                  <IcoButtonSimple
                    disabled={Boolean(selection.length === 0)}
                    id="btn-delete-standIn"
                    icon="icon-muelleimer-loeschen"
                    tooltipText={t('software.delete')}
                    handleClick={() => handleDeleteStandIn(selection)}
                  />
                  <SimpleButton
                    id="btn-add-standIn"
                    color="primary"
                    buttonText={t('standin.addStandin')}
                    handleClick={() => setNewStandInOpen(true)}
                  />
                </div>
              </div>

              <TableListEditableNew
                list={standInsUserSelected}
                selection={selection}
                columnId="standInId"
                onSelectionChange={(selectedRows) => setSelection(selectedRows as string[])}
                hasCheckbox
                onCellValueChanged={handleEditRow}
                columns={columnsStandins}
                columnSorting={[{ columnName: COLUMNS_NAMES.MB_INSIDE_CODE, direction: ASC_ORDER }]}
                loading={loading || loadingStandIns}
              />
            </>
          )}
        </div>
      </AccordionPanel>
      {isNewStandInOpen && (
        <DialogUsers
          title={t('standin.standin')}
          close={() => setNewStandInOpen(false)}
          updateUserSelected={(newStandIn: any) => addNewStandIn(newStandIn)}
          noResultsMessage="standin.noResults"
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  plants: getPlantsForm(state),
});

const actionCreators = {
  setPlants: setPlantsForm,
};

export default connect(mapStateToProps, actionCreators)(StandIndsAdmin);
